import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SportsIcon from '@mui/icons-material/SportsMartialArts';
import DefaultIcon from '@mui/icons-material/ViewList';
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Menu, useCreatePath } from 'react-admin';
import { useLocation } from 'react-router-dom';

export const EXERCISE_SUBMENU_RESOURCE_NAMES = [
  'ExerciseTypeCategoryGroup',
  'ExerciseTypeCategory',
  'ExerciseType',
  'ExerciseBase',
  'Exercise',
] as const;

export type ExerciseSubmenuResourceName =
  (typeof EXERCISE_SUBMENU_RESOURCE_NAMES)[number];

const EXERCISES_SECTION_SIDEBAR_LABEL = 'Movements & Activities';

function isExerciseSubmenuPath(pathname: string): boolean {
  const base = '/app/';
  return EXERCISE_SUBMENU_RESOURCE_NAMES.some(
    (name) =>
      pathname === `${base}${name}` || pathname.startsWith(`${base}${name}/`),
  );
}

type ResourceDefinition = {
  name: string;
  options?: { label?: string };
  icon?: React.ComponentType;
};

type ExercisesSubmenuProps = {
  resources: ResourceDefinition[];
  defaultExpanded?: boolean;
};

export const ExercisesSubmenu: React.FC<ExercisesSubmenuProps> = ({
  resources,
  defaultExpanded = false,
}) => {
  const location = useLocation();
  const createPath = useCreatePath();
  const isOpenByPath = isExerciseSubmenuPath(location.pathname);

  const [open, setOpen] = useState(() => isOpenByPath || defaultExpanded);

  useEffect(() => {
    if (isOpenByPath) {
      setOpen(true);
    }
  }, [isOpenByPath]);

  if (resources.length === 0) {
    return null;
  }

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={{ pl: 2 }}
        aria-expanded={open}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <SportsIcon />
        </ListItemIcon>
        <ListItemText primary={EXERCISES_SECTION_SIDEBAR_LABEL} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box component="span" sx={{ display: 'block', pl: 3 }}>
          {resources.map((resource) => {
            const to = createPath({ resource: resource.name, type: 'list' });
            const label = resource.options?.label ?? resource.name;
            const Icon = resource.icon ?? DefaultIcon;
            return (
              <Menu.Item
                key={resource.name}
                primaryText={label}
                to={to}
                leftIcon={<Icon />}
              />
            );
          })}
        </Box>
      </Collapse>
    </>
  );
};
